import { FC, useCallback, useState } from 'react';
import { MemoryRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { QueryClientProvider } from '@tanstack/react-query';
import LeadCapture from '@components/LeadCapture/LeadCapture';
import ProductOffersPage from '@components/ProductOffers/ProductOffersPage';
import CheckoutPage from '@components/Checkout/CheckoutPage';
import OrderConfirmationPage from '@components/Checkout/OrderConfirmationPage';
import ContactFloatingAction from '@components/ContactFloatingAction';
import InProgressOrderModal from '@components/modals/InProgressOrderModal/InProgressOrderModal';
import { FeatureFlagProvider } from '@contexts/FeatureFlagContext';
import { SellConfigProvider, useSellConfig } from '@contexts/SellConfigContext';
import { SessionProvider, useSessionContext } from '@contexts/SessionContext';
import { FrontendConfigBootstrap } from '@router/FrontendConfigBootstrap';
import { TenantConfigProvider } from '@providers/TenantConfigProvider';
import { DynamicMuiThemeProvider } from '@providers/DynamicMuiThemeProvider';
import { useQualificationFlowStore } from '@store/qualificationStore/qualificationStore';
import { useStartNewOrder } from '@hooks/useStartNewOrder';
import { useEmptyCart } from '@hooks/useEmptyCart';
import { useInitialRoute } from '@hooks/useInitialRoute/useInitialRoute';
import { useCustomer } from '@contexts/CustomerContext';
import { closeCpqApp } from '@utils/sessionUtils';
import ThemeWrapper from '@providers/ThemeWrapper';
import type { EntryPoint, EntryRouterInnerProps, EntryRouterProps } from '@router/EntryRouter.types';
import { ROUTES } from '@constants/routes';

function toDefaultRoute(page?: EntryPoint): string {
  if (page === 'business') return ROUTES.BUSINESS;
  return ROUTES.RESIDENTIAL;
}

const Spinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
    <CircularProgress />
  </Box>
);

interface InitialRouteResolverProps {
  defaultRoute: string;
  enableExistingCustomerFlow: boolean;
  children: (initialRoute: string) => React.ReactNode;
}

const InitialRouteResolver: FC<InitialRouteResolverProps> = ({ defaultRoute, children }) => {
  const { initialRoute } = useInitialRoute(defaultRoute);

  if (!initialRoute) return <Spinner />;

  return <>{children(initialRoute)}</>;
};

const EntryRouterInner: FC<EntryRouterInnerProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const { branding } = useCustomer();
  const { sessionId } = useSessionContext();
  const resumeRoute = useQualificationFlowStore((s) => s.resumeRoute);
  const setQualificationFlow = useQualificationFlowStore((s) => s.setQualificationFlow);
  const { startNewOrder, isStartingNew } = useStartNewOrder();
  const { emptyCart, isCartLoading } = useEmptyCart(sessionId ?? '');
  const [resumeDismissed, setResumeDismissed] = useState(false);
  const showResumeModal = !resumeDismissed && !!resumeRoute && !isCartLoading;

  const handleContinueOrder = useCallback(() => {
    setResumeDismissed(true);
    setQualificationFlow((prev) => ({ ...prev, resumeRoute: null }));
  }, [setQualificationFlow]);

  const handleStartNewOrder = useCallback(async () => {
    await emptyCart();
    await startNewOrder();
    setResumeDismissed(true);
    navigate(ROUTES.RESIDENTIAL);
  }, [emptyCart, startNewOrder, navigate]);

  const handleClose = (_event?: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    closeCpqApp(onClose, branding.homepageUrl);
  };

  return (
    <>
      <Routes>
        <Route path={ROUTES.RESIDENTIAL} element={<ProductOffersPage onClose={handleClose} />} />
        <Route path={ROUTES.BUSINESS} element={<LeadCapture onClose={handleClose} />} />
        <Route path={ROUTES.CHECKOUT} element={<CheckoutPage />} />
        <Route path={ROUTES.DONE} element={<OrderConfirmationPage onClose={onClose} />} />
        <Route path="*" element={<Navigate to={ROUTES.RESIDENTIAL} replace />} />
      </Routes>

      <InProgressOrderModal
        open={showResumeModal}
        onClose={handleContinueOrder}
        onContinuePreviousOrder={handleContinueOrder}
        onStartNewOrder={handleStartNewOrder}
        isLoading={isStartingNew}
      />

      <ContactFloatingAction />
    </>
  );
};

// Rendered only after SellConfigProvider validates config — safe to call useSellConfig().
// QueryClientProvider is here (not lifted): SellConfigProvider above has already
// configured SELL_API, so the tenant-config query fires with the correct baseURL.
// TenantConfigProvider fetches the theme, then DynamicMuiThemeProvider re-themes
// the whole app subtree over ThemeWrapper's static default theme.
type EntryRouterProvidersProps = Omit<
  EntryRouterProps,
  'apiBaseUrl' | 'tenantId' | 'mapsApiKey' | 'emotionContainer'
> & { isShadowDom?: boolean };

const EntryRouterProviders: FC<EntryRouterProvidersProps> = ({
  open = true,
  page,
  onClose,
  enableExistingCustomerFlow = false,
  isShadowDom = false,
}) => {
  const { queryClient } = useSellConfig();
  const defaultRoute = toDefaultRoute(page);

  return (
    <QueryClientProvider client={queryClient}>
      <TenantConfigProvider>
        <DynamicMuiThemeProvider isShadowDom={isShadowDom}>
          <FrontendConfigBootstrap />
          <FeatureFlagProvider enableExistingCustomerFlow={enableExistingCustomerFlow}>
            <SessionProvider>
              <InitialRouteResolver defaultRoute={defaultRoute} enableExistingCustomerFlow={enableExistingCustomerFlow}>
                {(initialRoute) => (
                  <MemoryRouter
                    initialEntries={[initialRoute]}
                    future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
                  >
                    <EntryRouterInner open={open} onClose={onClose} />
                  </MemoryRouter>
                )}
              </InitialRouteResolver>
            </SessionProvider>
          </FeatureFlagProvider>
        </DynamicMuiThemeProvider>
      </TenantConfigProvider>
    </QueryClientProvider>
  );
};

const EntryRouter = ({
  open = true,
  page,
  onClose,
  enableExistingCustomerFlow = false,
  apiBaseUrl,
  tenantId,
  mapsApiKey,
  emotionContainer,
}: EntryRouterProps) => (
  <ThemeWrapper emotionContainer={emotionContainer}>
    <SellConfigProvider apiBaseUrl={apiBaseUrl} tenantId={tenantId} mapsApiKey={mapsApiKey}>
      <EntryRouterProviders
        open={open}
        page={page}
        onClose={onClose}
        enableExistingCustomerFlow={enableExistingCustomerFlow}
        isShadowDom={emotionContainer !== undefined}
      />
    </SellConfigProvider>
  </ThemeWrapper>
);

export default EntryRouter;
